// @flow

import moment from "moment"
import _ from "lodash"
import fp from "lodash/fp"
import keymirror from "keymirror"
import { differenceInYears } from "date-fns"

import type { Insurance } from "common/consts/insurance"
import type {
  DocumentId,
  PassengerId,
  PassengerName,
  PriceTotal,
  PassengerTitle,
} from "mmb/types/primitiveTypes"

const createInitial = fp.compose(
  fp.capitalize,
  fp.first,
)
const capitalizeLastName = fp.compose(
  fp.capitalize,
  fp.last,
  fp.split(" "),
)

export const PASSENGER_CATEGORIES = keymirror({
  infant: null,
  child: null,
  teen: null,
  adult: null,
})

export const NATIONALITY_BLACKLIST = ["cs", "an"]

export type PassengerCategory = "infant" | "child" | "teen" | "adult"
export type SafePassengerCategory = "infant" | "child" | "adult"

export type CountryCode =
  | "ad"
  | "ae"
  | "af"
  | "ag"
  | "ai"
  | "al"
  | "am"
  | "an"
  | "anywhere"
  | "ao"
  | "aq"
  | "ar"
  | "as"
  | "at"
  | "au"
  | "aw"
  | "ax"
  | "az"
  | "ba"
  | "bb"
  | "bd"
  | "be"
  | "bf"
  | "bg"
  | "bh"
  | "bi"
  | "bj"
  | "bl"
  | "bm"
  | "bn"
  | "bo"
  | "bq"
  | "br"
  | "bs"
  | "bt"
  | "bv"
  | "bw"
  | "by"
  | "bz"
  | "ca-fr"
  | "ca"
  | "cc"
  | "cd"
  | "cf"
  | "cg"
  | "ch"
  | "ci"
  | "ck"
  | "cl"
  | "cm"
  | "cn"
  | "co"
  | "cr"
  | "cs"
  | "ct"
  | "cu"
  | "cv"
  | "cw"
  | "cx"
  | "cy"
  | "cz"
  | "de"
  | "dj"
  | "dk"
  | "dm"
  | "do"
  | "dz"
  | "ec"
  | "ee"
  | "eg"
  | "eh"
  | "er"
  | "es"
  | "et"
  | "eu"
  | "fi"
  | "fj"
  | "fk"
  | "fm"
  | "fo"
  | "fr"
  | "ga"
  | "gb"
  | "gd"
  | "ge"
  | "gf"
  | "gg"
  | "gh"
  | "gi"
  | "gl"
  | "gm"
  | "gn"
  | "gp"
  | "gq"
  | "gr"
  | "gs"
  | "gt"
  | "gu"
  | "gw"
  | "gy"
  | "hk"
  | "hm"
  | "hn"
  | "hr"
  | "ht"
  | "hu"
  | "ic"
  | "id"
  | "ie"
  | "il"
  | "im"
  | "in"
  | "io"
  | "iq"
  | "ir"
  | "is"
  | "it"
  | "je"
  | "jm"
  | "jo"
  | "jp"
  | "ke"
  | "kg"
  | "kh"
  | "ki"
  | "km"
  | "kn"
  | "kp"
  | "kr"
  | "kw"
  | "ky"
  | "kz"
  | "la"
  | "lb"
  | "lc"
  | "li"
  | "lk"
  | "lr"
  | "ls"
  | "lt"
  | "lu"
  | "lv"
  | "ly"
  | "ma"
  | "mc"
  | "md"
  | "me"
  | "mf"
  | "mg"
  | "mh"
  | "mk"
  | "ml"
  | "mm"
  | "mn"
  | "mo"
  | "mp"
  | "mq"
  | "mr"
  | "ms"
  | "mt"
  | "mu"
  | "mv"
  | "mw"
  | "mx"
  | "my"
  | "mz"
  | "na"
  | "nc"
  | "ne"
  | "nf"
  | "ng"
  | "ni"
  | "nl"
  | "no"
  | "np"
  | "nr"
  | "nu"
  | "nz"
  | "om"
  | "pa"
  | "pe"
  | "pf"
  | "pg"
  | "ph"
  | "pk"
  | "pl"
  | "pm"
  | "pn"
  | "pr"
  | "ps"
  | "pt"
  | "pw"
  | "py"
  | "qa"
  | "re"
  | "ro"
  | "rs"
  | "ru"
  | "rw"
  | "sa"
  | "sb"
  | "sc"
  | "sd"
  | "se"
  | "sg"
  | "sh"
  | "si"
  | "sj"
  | "sk"
  | "sl"
  | "sm"
  | "sn"
  | "so"
  | "sr"
  | "ss"
  | "st"
  | "sv"
  | "sx"
  | "sy"
  | "sz"
  | "tc"
  | "td"
  | "tf"
  | "tg"
  | "th"
  | "tj"
  | "tk"
  | "tl"
  | "tm"
  | "tn"
  | "to"
  | "tr"
  | "tt"
  | "tv"
  | "tw"
  | "tz"
  | "ua"
  | "ug"
  | "um"
  | "us"
  | "uy"
  | "uz"
  | "va"
  | "vc"
  | "ve"
  | "vg"
  | "vi"
  | "vn"
  | "vu"
  | "wf"
  | "ws"
  | "xk"
  | "ye"
  | "yt"
  | "za"
  | "zm"
  | "zw"

export type Advice = {
  status: string,
  country: CountryCode,
  info: ?string,
  type: string,
}

type PBOrderedFlightData = {|
  currency: string,
  price: number,
  is_ordered: boolean,
|}

type PBOrderedData = {
  [flightId: string]: PBOrderedFlightData,
}

export type Document = {
  code: ?string,
  expiration: ?moment,
}

export type PassengerArgs = {
  id: number,
  category: PassengerCategory,
  title: PassengerTitle,
  firstname: PassengerName,
  middlename: PassengerName,
  lastname: PassengerName,
  nationality: string,

  birth: moment,
  document: Document,
  insurance: Insurance,
  insurancePrice: number,

  advices: Array<Advice>,
  bags: number,
  baggage: {
    handBag: number,
    holdBag: number,
  },
  handBags: number,
  extraHandBags: number,
  priorityBoarding: PBOrderedData,
}

export default class Passenger {
  id: PassengerId

  category: PassengerCategory

  title: PassengerTitle

  firstname: PassengerName

  middlename: PassengerName

  lastname: PassengerName

  // $FlowFixMe
  nationality: string

  birth: moment

  document: {
    code: ?DocumentId,
    expiration: ?moment,
  }

  insurance: Insurance

  insurancePrice: PriceTotal

  advices: Array<Advice>

  bags: number

  baggage: {
    handBag: number,
    holdBag: number,
  }

  handBags: number

  extraHandBags: number

  hasOrderedPB: boolean

  priorityBoarding: PBOrderedData

  constructor(args: PassengerArgs) {
    this.id = args.id // number

    this.category = args.category // string
    this.title = args.title // string
    this.firstname = args.firstname // string
    this.middlename = args.middlename // string
    this.lastname = args.lastname // string
    this.nationality = args.nationality // 2 character string

    this.birth = args.birth // moment
    this.document = args.document // {code: string, expiration: moment}

    this.insurance = args.insurance // string
    this.insurancePrice = args.insurancePrice // number

    this.advices = args.advices // object
    this.bags = args.bags // object
    this.baggage = args.baggage // object
    this.handBags = args.handBags // number
    this.extraHandBags = args.extraHandBags // number
    this.priorityBoarding = args.priorityBoarding // number
  }

  getNameWithoutTitle(): string {
    return Passenger.formatNameWithoutTitle(this)
  }

  getFullName(): string {
    return Passenger.formatName(this)
  }

  getFullNameWithoutTitle(): string {
    return Passenger.formatNameWithoutTitle(this)
  }

  getShortName(): string {
    return Passenger.formatShortName(this)
  }

  getInitials(): string {
    return Passenger.formatInitials(this)
  }

  hasBagsAllowed(bagsAllowedForInfants: boolean) {
    const day = this.birth.date()
    const month = this.birth.month() + 1 // fucking JS month indexing
    const year = this.birth.year()

    return Passenger.hasBagsAllowedStatic(day, month, year, bagsAllowedForInfants)
  }

  // Type of passenger can be related to some milestone (e.g. journeyDepartureMoment) by default milestone = moment()
  // startOf("day") is really important
  static determineTypeFromInput(
    day: number,
    month: number,
    year: number,
    milestone: moment = moment().startOf("day"),
    { adult, teen, child }: { adult?: number, teen?: number, child?: number } = {},
    enableUndefined: boolean = false,
  ) {
    const adultThreshold = adult || 18
    const teenThreshold = teen || adultThreshold // Same as adult => no teen category
    const childThreshold = child || 2

    if (day && month && year) {
      const birthdayMoment = moment(`${year}-${month}-${day}`, "YYYY-MM-DD")

      if (!birthdayMoment.isValid() || year.toString().length < 4) {
        return PASSENGER_CATEGORIES.adult
      }

      const adultThresholdMoment = milestone.clone().subtract(adultThreshold, "years")
      const teenThresholdMoment = milestone.clone().subtract(teenThreshold, "years")
      const childThresholdMoment = milestone.clone().subtract(childThreshold, "years")

      /**
       * ADULT  >= adultThreshold or < now() - we do not have "invalid" passenger category
       * TEEN   < adultThreshold
       * CHILD  < teenThreshold
       * INFANT < childThreshold
       */

      if (birthdayMoment.isBefore(adultThresholdMoment) || birthdayMoment.isAfter(moment())) {
        return PASSENGER_CATEGORIES.adult
      }
      if (birthdayMoment.isBefore(teenThresholdMoment)) {
        return PASSENGER_CATEGORIES.teen
      }
      if (birthdayMoment.isBefore(childThresholdMoment)) {
        return PASSENGER_CATEGORIES.child
      }
      return PASSENGER_CATEGORIES.infant
    }

    if (enableUndefined) {
      return undefined
    }

    // Default type of passenger should be "adult"
    return PASSENGER_CATEGORIES.adult
  }

  static determineAgeFromInput(day: number, month: number, year: number, NOW: Date = new Date()) {
    return differenceInYears(NOW, new Date(`${year}-${month}-${day}`))
  }

  static hasBagsAllowedStatic(
    day: number,
    month: number,
    year: number,
    bagsAllowedForInfants: boolean,
  ) {
    return (
      Passenger.determineTypeFromInput(day, month, year) !== PASSENGER_CATEGORIES.infant ||
      bagsAllowedForInfants
    )
  }

  static formatName(passenger: Passenger): string {
    const { title, firstname, middlename, lastname } = passenger

    return _.compact([
      // Compact due to middlename should be undefined
      _.capitalize(title),
      firstname,
      middlename,
      lastname,
    ]).join(" ")
  }

  static formatNameWithoutTitle(passenger: Passenger): string {
    const { firstname, middlename, lastname } = passenger

    return _.compact([
      // Compact due to middlename should be undefined
      firstname,
      middlename,
      lastname,
    ]).join(" ")
  }

  static formatShortName(passenger: Passenger): string {
    // $FlowFixMe: error revealed by updating Flow and lodash libdef, please fix
    return `${createInitial(passenger.firstname)}. ${capitalizeLastName(passenger.lastname)}`
  }

  static formatInitials(passenger: Passenger): string {
    // $FlowFixMe: error revealed by updating Flow and lodash libdef, please fix
    return `${createInitial(passenger.firstname)}${createInitial(passenger.lastname)}`
  }

  // eslint-disable-next-line flowtype/no-weak-types
  static equals(a: Object, b: Object) {
    return (
      a.firstname === b.firstname &&
      a.lastname === b.lastname &&
      // Passenger doesn't have these arguments
      // TODO: add flow types to mark what is being compared here, or rewrite
      a.birthDay === b.birthDay &&
      a.birthMonth === b.birthMonth &&
      a.birthYear === b.birthYear
    )
  }

  static equalNames(a: Passenger, b: Passenger) {
    return a.firstname === b.firstname && a.lastname === b.lastname
  }

  // eslint-disable-next-line flowtype/no-weak-types
  static fake(args: Object) {
    const defaults = {
      id: 123,
      category: "adult",
      title: "mr",
      firstname: "Test",
      middlename: "Testovic",
      lastname: "Tester",
      nationality: "ru",
      birth: moment("1990-01-01"),
      document: {
        code: "123ABC",
        expiration: moment("2020-01-01"),
      },
      insurance: "none",
      insurancePrice: 10,
      advices: [],
      bags: 1,
      handBags: 1,
      extraHandBags: 1,
    }

    return new Passenger({ ...defaults, ...args })
  }
}
